/**
 * Centralised react-query keys for the HRManagement module. Co-locating them
 * lets us invalidate consistently after mutations.
 */

/**
 * Leave approval chain keys (api/leave-approvals and api/leave-approval-rules).
 * Everything sits under `['hr', 'leave-approvals']`, so invalidating
 * `hrKeys.leaveApprovals.all()` refreshes every approval surface at once.
 * Built from a standalone root so the nested object does not reference itself.
 */
const LEAVE_APPROVALS_ROOT = ['hr', 'leave-approvals'] as const;

const leaveApprovalKeys = {
  all: () => LEAVE_APPROVALS_ROOT,
  capabilities: () => [...LEAVE_APPROVALS_ROOT, 'capabilities'] as const,
  pendingAll: () => [...LEAVE_APPROVALS_ROOT, 'pending'] as const,
  pending: (tab: string) => [...LEAVE_APPROVALS_ROOT, 'pending', tab] as const,
  historyAll: () => [...LEAVE_APPROVALS_ROOT, 'history'] as const,
  history: (page: number, pageSize: number = 20) => [...LEAVE_APPROVALS_ROOT, 'history', page, pageSize] as const,
  hrQueue: () => [...LEAVE_APPROVALS_ROOT, 'hr-queue'] as const,
  item: (leaveId: number) => [...LEAVE_APPROVALS_ROOT, 'item', leaveId] as const,
  rulesAll: () => [...LEAVE_APPROVALS_ROOT, 'rules'] as const,
  rules: (scopeId?: number | null, includeInactive?: boolean) =>
    [...LEAVE_APPROVALS_ROOT, 'rules', scopeId ?? 'all', includeInactive ? 'with-inactive' : 'active'] as const,
  preview: (userId: string) => [...LEAVE_APPROVALS_ROOT, 'preview', userId] as const,
  health: () => [...LEAVE_APPROVALS_ROOT, 'health'] as const,
  departments: () => [...LEAVE_APPROVALS_ROOT, 'departments'] as const,
  lookups: () => [...LEAVE_APPROVALS_ROOT, 'lookups'] as const,
};

export const hrKeys = {
  all: ['hr'] as const,

  // Leave approval chain (line approver inbox, HR final queue, rules)
  leaveApprovals: leaveApprovalKeys,

  // Admin
  employees: () => [...hrKeys.all, 'employees'] as const,
  employee: (id: string) => [...hrKeys.employees(), id] as const,
  employeeFiles: () => [...hrKeys.all, 'employee-files'] as const,
  employeeFile: (id: number) => [...hrKeys.employeeFiles(), id] as const,
  leaves: (filters?: Record<string, unknown>) =>
    filters ? ([...hrKeys.all, 'leaves', filters] as const) : ([...hrKeys.all, 'leaves'] as const),
  leave: (id: number) => [...hrKeys.all, 'leave', id] as const,
  leaveDocuments: () => [...hrKeys.all, 'leave-documents'] as const,
  leaveDocumentsByLeave: (leaveId: number) => [...hrKeys.all, 'leave-documents', 'by-leave', leaveId] as const,
  policies: () => [...hrKeys.all, 'policies'] as const,
  policy: (id: number) => [...hrKeys.policies(), id] as const,
  policyAssignments: (filters?: Record<string, unknown>) =>
    filters ? ([...hrKeys.all, 'policy-assignments', filters] as const) : ([...hrKeys.all, 'policy-assignments'] as const),
  policyAssignment: (id: number) => [...hrKeys.all, 'policy-assignment', id] as const,
  policySignatures: () => [...hrKeys.all, 'policy-signatures'] as const,
  hrSettings: () => [...hrKeys.all, 'settings', 'hr'] as const,
  notificationSettings: () => [...hrKeys.all, 'settings', 'notifications'] as const,
  paySpaceSync: () => [...hrKeys.all, 'payspace-sync'] as const,

  // Lookups (shared between admin + staff portal)
  duties: () => [...hrKeys.all, 'duties'] as const,
  employeeDuties: (userId: string) => [...hrKeys.all, 'employee-duties', userId] as const,
  leaveTypes: () => [...hrKeys.all, 'lookups', 'leave-types'] as const,
  leaveStatuses: () => [...hrKeys.all, 'lookups', 'leave-statuses'] as const,
  leaveDocumentTypes: () => [...hrKeys.all, 'lookups', 'leave-document-types'] as const,
  fileTypes: () => [...hrKeys.all, 'lookups', 'file-types'] as const,
  fileCategories: () => [...hrKeys.all, 'lookups', 'file-categories'] as const,
  employeeTypes: () => [...hrKeys.all, 'lookups', 'employee-types'] as const,
  employeeStatuses: () => [...hrKeys.all, 'lookups', 'employee-statuses'] as const,
  policyCategories: () => [...hrKeys.all, 'lookups', 'policy-categories'] as const,

  // Staff portal
  myProfile: () => [...hrKeys.all, 'my', 'profile'] as const,
  myMember: () => [...hrKeys.all, 'my', 'member'] as const,
  myMemberStatus: () => [...hrKeys.all, 'my', 'member-status'] as const,
  myAllergies: () => [...hrKeys.all, 'my', 'allergies'] as const,
  myDisabilities: () => [...hrKeys.all, 'my', 'disabilities'] as const,
  myAddresses: () => [...hrKeys.all, 'my', 'addresses'] as const,
  maritalStatuses: () => [...hrKeys.all, 'lookups', 'marital-statuses'] as const,
  allergies: () => [...hrKeys.all, 'lookups', 'allergies'] as const,
  disabilities: () => [...hrKeys.all, 'lookups', 'disabilities'] as const,
  myLeaves: () => [...hrKeys.all, 'my', 'leaves'] as const,
  myLeave: (id: number) => [...hrKeys.all, 'my', 'leave', id] as const,
  myLeaveDocuments: (leaveId: number) => [...hrKeys.all, 'my', 'leave-documents', leaveId] as const,
  myFiles: () => [...hrKeys.all, 'my', 'files'] as const,
  myDuties: () => [...hrKeys.all, 'my', 'duties'] as const,
  myAssignments: (status?: string) =>
    status ? ([...hrKeys.all, 'my', 'assignments', status] as const) : ([...hrKeys.all, 'my', 'assignments'] as const),
  myAssignment: (id: number) => [...hrKeys.all, 'my', 'assignment', id] as const,
  mySignatures: () => [...hrKeys.all, 'my', 'signatures'] as const,
  publishedPolicies: () => [...hrKeys.all, 'my', 'published-policies'] as const,
  publishedPolicy: (id: number) => [...hrKeys.all, 'my', 'published-policy', id] as const,

  // Announcements + calendar (Phase 7c)
  announcements: () => [...hrKeys.all, 'announcements'] as const,
  announcement: (id: number) => [...hrKeys.all, 'announcement', id] as const,
  myAnnouncements: (take?: number) =>
    take != null ? ([...hrKeys.all, 'my', 'announcements', take] as const) : ([...hrKeys.all, 'my', 'announcements'] as const),
  announcementTypes: () => [...hrKeys.all, 'lookups', 'announcement-types'] as const,
  announcementStatuses: () => [...hrKeys.all, 'lookups', 'announcement-statuses'] as const,
  publicHolidays: (upcoming?: boolean) =>
    upcoming ? ([...hrKeys.all, 'public-holidays', 'upcoming'] as const) : ([...hrKeys.all, 'public-holidays'] as const),
  birthdays: (month?: number, todayOnly?: boolean) =>
    [...hrKeys.all, 'birthdays', month ?? 'all', todayOnly ? 'today' : 'all'] as const,
};
